import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PaperClipIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { getSignedUrl } from '../../../../../services/s3Service';
import { formatFileSize } from '../../../../../utils/formatters';

const EmailAttachment = ({ attachment }) => {
  const { filename, mime_type, size, s3_key, s3_url } = attachment;
  const [currentUrl, setCurrentUrl] = useState(s3_url);
  const isRenewingRef = useRef(false);

  const isImage = mime_type?.startsWith('image/');

  // Function to renew S3 signed URL
  const renewSignedUrl = useCallback(async () => {
    if (!s3_key || isRenewingRef.current) return null;

    isRenewingRef.current = true;
    try {
      const signedUrl = await getSignedUrl(s3_key);
      if (signedUrl) {
        isRenewingRef.current = false;
        return signedUrl;
      }
    } catch (error) {
      console.error('Error renewing signed URL:', error);
    }
    isRenewingRef.current = false;
    return null;
  }, [s3_key]);

  useEffect(() => {
    if (s3_key) {
      const initializeUrl = async () => {
        const freshUrl = await renewSignedUrl();
        if (freshUrl) {
          setCurrentUrl(freshUrl);
        }
      };
      initializeUrl();
    }
  }, [s3_key, renewSignedUrl]);

  const handleClick = async e => {
    if (s3_key && currentUrl) {
      const renewedUrl = await renewSignedUrl();
      if (renewedUrl && renewedUrl !== currentUrl) {
        e.preventDefault();
        setCurrentUrl(renewedUrl);
        window.open(renewedUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const Icon = isImage ? PhotoIcon : DocumentIcon;

  return (
    <a
      href={currentUrl}
      target='_blank'
      rel='noreferrer'
      onClick={handleClick}
      className='flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group'
    >
      <div className='p-2 bg-white rounded-lg shadow-sm group-hover:shadow'>
        <Icon className='w-5 h-5 text-gray-500' />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-gray-700 truncate'>{filename || 'Archivo'}</p>
        <p className='text-xs text-gray-500'>
          {mime_type && <span>{mime_type}</span>}
          {size && <span className='ml-2'>{formatFileSize(size)}</span>}
        </p>
      </div>
      <PaperClipIcon className='w-4 h-4 text-gray-400 flex-shrink-0' />
    </a>
  );
};

export default EmailAttachment;
